import { Form, Input, message, Modal, Select } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import TextArea from 'antd/lib/input/TextArea';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import { areasRestCrudThunksForSupplier } from '../../thunks/areas_thunks';
import { assetTypesRestCrudThunksForSupplier } from '../../thunks/asset_types_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import ModalFormFooter from '../common/ModalFormFooter';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { DateTimeWithTimeZonev4 } from '../date_time_with_time_zone/DateTimeWithTimeZonev4';
import { wallTimeToUtcIso, BE_DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import { scheduleEPSLTransfer } from '../../thunks/equipment_per_stock_locations_thunks';
import { createAssetFromEquipmentPerStockLocationBulkForSupplier } from '../../thunks/assets_thunks';
import { assetModelsRestCrudThunksForSupplier } from '../../thunks/asset_models_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';

const LOC_TC_NAME = 'scheduledTransferDropdown';
const AREA_TC_NAME = 'scheduledTransferDropdown';
const AT_TC_NAME = 'scheduledTransferDropdown';
const FORM_ID = 'epsl-scheduled-transfer-loc-form';

const EPSLScheduledTransferToLocationModal: FC<any> = ({
	epslIds,
	onCancel,
	onSuccess,
	initialRecord,
	locations,
	areas,
	assetTypes,
	companyConfig,
	fetchLocations,
	fetchMultipleLocations,
	fetchAreas,
	fetchMultipleAreas,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	scheduleTransfer,
	fetchAssetModels,
	assetModels,
	bulkTransferToLocation,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [saving, setSaving] = useState(false);

	const formInitialValues = useMemo(
		() => ({
			...(nullSafeGet('equipmentType.assetTypeId', initialRecord)
				? { assetTypeId: nullSafeGet('equipmentType.assetTypeId', initialRecord) }
				: {}),
		}),
		[initialRecord]
	);

	const transferNoteRequired = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.transferNoteMandatory', companyConfig, false),
		[companyConfig]
	);

	const selectedLocationId = Form.useWatch('locationId', form);
	const triggerAtValue = Form.useWatch('triggerAt', form);

	const selectedLocation = useMemo(() => {
		if (!selectedLocationId) return null;
		const records = getRecordsForTargetCollection(locations, LOC_TC_NAME);
		return records.find((_) => _.id === selectedLocationId) || null;
	}, [selectedLocationId, locations]);

	const isFutureDate = useMemo(
		() => triggerAtValue && moment(triggerAtValue).isAfter(moment()),
		[triggerAtValue]
	);

	useEffect(() => {
		if (!selectedLocationId) return;
		form.setFieldsValue({ areaId: undefined });
	}, [selectedLocationId, form]);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);

			if (!isFutureDate) {
				const transferDate = values.triggerAt
					? moment(values.triggerAt).format(BE_DATE_ONLY_FORMAT)
					: undefined;
				const entities = epslIds.map((equipmentPerStockLocationId) => ({
					equipmentPerStockLocationId,
					locationId: values.locationId,
					assetTypeId: values.assetTypeId,
					...(values.assetModelId && { assetModelId: values.assetModelId }),
					transferDate,
					...(values.transferNote && { transferNote: values.transferNote }),
				}));
				bulkTransferToLocation(entities)
					.then(onSuccess)
					.catch(message.error)
					.finally(() => setSaving(false));
				return;
			}

			const triggerAt =
				values.triggerAt &&
				wallTimeToUtcIso(values.triggerAt, values.transferTimezone || moment.tz.guess());

			scheduleTransfer({
				triggerAt,
				equipmentPerStockLocationIds: epslIds,
				locationId: values.locationId,
				assetTypeId: values.assetTypeId,
				...(values.areaId && { areaId: values.areaId }),
				...(values.transferNote && { transferNote: values.transferNote }),
			})
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[epslIds, isFutureDate, scheduleTransfer, bulkTransferToLocation, onSuccess]
	);

	return (
		<Modal
			visible={true}
			title="Transfer to location"
			onCancel={onCancel}
			footer={null}
			closable={true}
		>
			<Form
				id={FORM_ID}
				layout="vertical"
				form={form}
				onFinish={handleSubmit}
				initialValues={{
					...(nullSafeGet('equipmentType.assetTypeId', initialRecord)
						? { assetTypeId: nullSafeGet('equipmentType.assetTypeId', initialRecord) }
						: {}),
					triggerAt: moment().startOf('hour'),
				}}
			>
				<Form.Item
					name="locationId"
					label="Location"
					rules={[{ required: true, message: 'Location is required!' }]}
				>
					<OWAsyncSelect
						stateSlice={locations}
						targetCollectionName={LOC_TC_NAME}
						fetchMultiple={fetchMultipleLocations}
						fetchData={(searchText, targetCollectionName) =>
							fetchLocations({ name: searchText }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option key={record.id} value={record.id}>
								{record.name}
							</Select.Option>
						)}
					/>
				</Form.Item>
				<Form.Item name="areaId" label="Area">
					<OWAsyncSelect
						disabled={!selectedLocationId}
						stateSlice={areas}
						targetCollectionName={AREA_TC_NAME}
						fetchMultiple={fetchMultipleAreas}
						fetchData={(searchText, targetCollectionName) =>
							fetchAreas({ name: searchText, locationId: selectedLocationId }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option key={record.id} value={record.id}>
								{record.name}
							</Select.Option>
						)}
						additionalFilters={{ locationId: selectedLocationId }}
					/>
				</Form.Item>

				<div className="mb-6">
					<DateTimeWithTimeZonev4
						form={form}
						location={selectedLocation}
						displayName="Effective transfer date"
						dateName="triggerAt"
						timeZoneName="transferTimezone"
						rules={[{ required: true, message: 'Transfer date is required.' }]}
						help={'Future-dated transfers will be executed on the chosen date & time.'}
						showTime={{ format: 'h A', showMinute: false, showSecond: false }}
						required
						showNow={false}
					/>
				</div>
				<Form.Item
					name="assetTypeId"
					label="Asset Type"
					rules={[{ required: true, message: 'Asset type is required!' }]}
				>
					<OWAsyncSelect
						stateSlice={assetTypes}
						targetCollectionName={AT_TC_NAME}
						fetchMultiple={fetchMultipleAssetTypes}
						fetchData={(searchText, targetCollectionName) =>
							fetchAssetTypes({ name: searchText }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option key={record.id} value={record.id}>
								{record.name}
							</Select.Option>
						)}
					/>
				</Form.Item>
				<Form.Item name="assetModelId" label="Asset Model">
					<OWAsyncSelect
						stateSlice={assetModels}
						targetCollectionName={'bulkEditAssetsAssetModels'}
						fetchMultiple={() => {}}
						fetchData={(searchText, targetCollectionName) =>
							fetchAssetModels({ name: searchText }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option key={record.id} value={record.id}>
								{record.modelName}
							</Select.Option>
						)}
					/>
				</Form.Item>

				<Form.Item
					name="transferNote"
					label="Transfer Note"
					rules={[{ required: transferNoteRequired, message: 'Please enter transfer note.' }]}
				>
					<TextArea autoSize={{ minRows: 3 }} />
				</Form.Item>
				<ModalFormFooter
					okText={isFutureDate ? 'Schedule Transfer' : 'Transfer'}
					cancelText="Cancel"
					loading={saving}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	locations: state.locations,
	areas: state.areas,
	assetTypes: state.asset_types,
	companyConfig: state.company_config.detail,
	assetModels: state.asset_models,
});

const mapDispatchToProps = (dispatch) => ({
	fetchLocations: (params, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAreas: (params, targetCollectionName) =>
		dispatch(areasRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleAreas: (ids, targetCollectionName) =>
		dispatch(areasRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAssetTypes: (params, targetCollectionName) =>
		dispatch(assetTypesRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleAssetTypes: (ids, targetCollectionName) =>
		dispatch(assetTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	scheduleTransfer: (data) => dispatch(scheduleEPSLTransfer(ROLE_TYPES.SUPPLIER)(data)),
	bulkTransferToLocation: (entities) =>
		dispatch(createAssetFromEquipmentPerStockLocationBulkForSupplier(entities)),
	fetchAssetModels: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetModelsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				'bulkEditAssetsAssetModels',
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(EPSLScheduledTransferToLocationModal) as React.FC<any>;
